<script setup lang="ts">
import type { ReportImage } from "@/types/report-images.types";
import { useDropZone } from "../findings/useDragItem";
import VisualiseImage from "../findings/VisualiseImage.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";
import { useTranslation } from "i18next-vue";
import { ref } from "vue";

defineProps<{
  images: ReportImage[];
  diagnosticId: number;
}>();

const emit = defineEmits<{
  add: [];
  remove: [imageId: number];
  drop: [imageId: number];
  edit: [image: ReportImage];
}>();

const { t } = useTranslation();

const showRemoveConfirm = ref(false);
const imageToRemove = ref<number | null>(null);

const askRemove = (imageId: number) => {
  imageToRemove.value = imageId;
  showRemoveConfirm.value = true;
};

const confirmRemove = () => {
  if (imageToRemove.value !== null) {
    emit("remove", imageToRemove.value);
  }
  showRemoveConfirm.value = false;
  imageToRemove.value = null;
};

const { onDrop, dragover, onDragover, onDragleave } = useDropZone(
  async ({ item, type }) => {
    if (type === "image") {
      emit("drop", item.id);
    }
  },
);
</script>

<template>
  <div class="overview-drop-zone d-flex gap-3 flex-wrap align-items-start">
    <!-- Existing images -->
    <div
      v-for="image in images"
      :key="image.id"
      class="overview-card position-relative"
    >
      <!-- Image with preview overlay -->
      <div class="overview-image">
        <VisualiseImage
          v-if="image.diagnosticImage?.file"
          :key="image.diagnosticImage.file.id"
          :id="image.diagnosticImage.file.id"
          fill
          class="w-100 h-100"
          previewMode="overlay"
        />
      </div>
      <DRBtnIcon
        class="remove-btn position-absolute top-0 end-0 m-2"
        icon="bi-x-lg"
        variant="secondary"
        :size="28"
        @click.stop="askRemove(image.id)"
      />

      <!-- Badges below image -->
      <div
        class="d-flex gap-1 mt-1 justify-content-between align-items-center py-1"
      >
        <!-- Number + Description (clickable to edit) -->
        <div
          class="mx-1 px-1 description-line flex-grow-1 small mb-1 py-1 text-truncate gap-2 d-flex flex-row rounded px-1 clickable-description"
          @click.stop="emit('edit', image)"
        >
          <span v-if="image.numbering" class="text-muted"
            >#{{ image.numbering }}</span
          >
          <span v-else class="no-description">#--</span>
          <span v-if="image.description" class="text-muted text-truncate">
            {{ image.description }}
          </span>
          <span v-else class="no-description text-truncate">
            {{ t("report:photos.noDescription") }}
          </span>
        </div>
      </div>
    </div>

    <!-- Drop zone / Add button -->
    <div
      class="add-zone d-flex align-items-center justify-content-center rounded cursor-pointer"
      :class="{ 'drop-active': dragover }"
      @dragover.prevent.stop="onDragover"
      @dragleave.prevent.stop="onDragleave"
      @drop.stop="onDrop"
      @click="emit('add')"
    >
      <div class="text-center">
        <el-icon size="28" class="text-muted"
          ><i class="bi-plus-lg"></i
        ></el-icon>
        <div class="small text-muted mt-1">
          {{ $t("report:overviews.add") }}
        </div>
      </div>
    </div>

    <ConfirmationModal
      v-model="showRemoveConfirm"
      :title="t('report:overviews.removeConfirm.title')"
      :message="t('report:overviews.removeConfirm.message')"
      :confirmLabel="t('report:overviews.removeConfirm.confirm')"
      danger
      @confirm="confirmRemove"
    />
  </div>
</template>

<style lang="scss" scoped>
@import "@/style/variables.scss";

.overview-card {
  display: flex;
  flex-direction: column;
  width: 250px;

  .remove-btn {
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover .remove-btn {
    opacity: 1;
  }
}

.overview-image {
  width: 250px;
  height: 150px;
  border-radius: 8px;
  overflow: hidden;

  .action-buttons {
    top: 6px;
    right: 6px;
    opacity: 0;
    transition: opacity 0.2s;
    z-index: 2;
  }

  &:hover .action-buttons {
    opacity: 1;
  }

  .action-btn {
    transition: transform 0.15s;

    &:hover {
      transform: scale(1.1);
    }
  }
}

.overview-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 4px;
  white-space: nowrap;

  &.badge-active {
    background-color: rgba(25, 135, 84, 0.12);
    color: #198754;
  }

  &.badge-inactive {
    background-color: var(--bs-gray-200);
    color: var(--bs-gray-500);
  }
}

.add-zone {
  width: 250px;
  height: 150px;
  border: 2px dashed var(--bs-border-color);
  background-color: var(--bs-light);
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: var(--bs-primary);
    background-color: rgba($primary, 0.05);
  }

  &.drop-active {
    border-color: $primary;
    background-color: rgba($primary, 0.1);
  }
}
.description-line {
  line-height: 1.4;
  min-width: 0;
}

.clickable-description {
  cursor: pointer;
  transition: background-color 0.15s;
  margin-left: -4px;
  margin-right: -4px;

  &:hover {
    background-color: var(--bs-gray-200);
  }
}
</style>
